import { futureValueSIP } from "../../engine/calculations";

/**
 * Config-driven calculator — CLAUDE.md architecture rule: adding a calculator means writing
 * one of these, not a new page. The CalculatorRenderer turns this into a full form + results +
 * chart + disclaimer + save button.
 */
const sipFutureValueConfig = {
  key: "sip-future-value",
  title: "SIP Future Value",
  category: "Growth",
  description:
    "See how a monthly SIP grows over time, split into what you invested and what the market added.",
  fields: [
    {
      key: "monthly",
      label: "Monthly SIP amount",
      type: "currency",
      default: 10000,
      min: 500,
      max: 10_00_000,
      help: "How much you invest every month.",
    },
    {
      key: "returnRate",
      label: "Expected annual return",
      type: "percent",
      default: 12,
      min: 1,
      max: 30,
      assumptionKey: "equityReturn", // pre-fill from the central assumptions config
      help: "Long-term expected return, e.g. 12% for equity mutual funds.",
    },
    {
      key: "years",
      label: "Investment period (years)",
      type: "number",
      default: 15,
      min: 1,
      max: 40,
      help: "How long you plan to keep investing.",
    },
  ],
  chart: {
    type: "line",
    xKey: "year",
    series: [
      { key: "invested", label: "Invested", colorVar: "--muted" },
      { key: "value", label: "Value", colorVar: "--gold" },
    ],
  },
  /** values = raw form values (percent fields are whole numbers, e.g. 12 not 0.12). */
  compute(values) {
    const monthly = Number(values.monthly) || 0;
    const annualRate = (Number(values.returnRate) || 0) / 100;
    const years = Number(values.years) || 0;
    const months = Math.round(years * 12);

    const futureValue = futureValueSIP(monthly, annualRate, months);
    const totalInvested = monthly * months;
    const estimatedGains = futureValue - totalInvested;

    const series = [];
    for (let y = 1; y <= years; y++) {
      series.push({
        year: y,
        invested: monthly * y * 12,
        value: futureValueSIP(monthly, annualRate, y * 12),
      });
    }

    return {
      headline: { label: "Future Value", value: futureValue },
      breakdown: [
        { label: "Total Invested", value: totalInvested },
        { label: "Estimated Gains", value: estimatedGains },
      ],
      series,
    };
  },
};

export default sipFutureValueConfig;
